import React from "react";
import NoSleep from "nosleep.js";
import { useLocation } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useRedirectIfUnauthenticated } from "utils/azureAD/useRedirectIfUnauthenticated";
import CircularProgressWithLabel from "Shared.Components/CircularProgressWithLabel";
import { getSynchronizationItemsAsync } from "PdfDocumentGrid/pdfDocuments.service";
import { useMutateGetHomePageInfo } from "HomePage/apis/useGetHomePageInfo";
import FontAwesomeSvgIcon from "Layouts/Common/FontAwesomeSvgIcon";
import NoSleeepDialog from "Shared.Components/NoSleeepDialog";
import { isMobileDevice } from "utils/pwa/pwa.helpers";
import svgFonts from "Layouts/Common/fontAwesome";
import { Notifications } from "Notifications";
import { isPwa } from "utils/pwa";

import {
  Box,
  Button,
  Toolbar,
  SvgIcon,
  useTheme,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
} from "@mui/material";

import { updateSettings, getCountPdfDocumentsMeta, getAllSynchronizationItemsMeta } from "utils/DexieDBManager";

import { useSettings, VERSIONS_API_REQUEST_ACTIONS } from "SettingsPage/Settings.Context";

import { ReactComponent as WifiSlash } from "assets/images/wifi-slash.svg";
import { ReactComponent as FlagFR } from "assets/images/flag-fr.svg";
import { ReactComponent as FlagUK } from "assets/images/flag-uk.svg";
import useOnlineStatus from "utils/InternetChecker/useOnlineStatus";
import LogoGETeDOCS from "assets/images/logo_GETeDOCS.png";
import { stylesRefresh, AppBar } from "./Header.styles";
import LogoGEDOC from "assets/images/logo_GEDOC.png";
import { LANGUAGES } from "utils/i18n/resources";
import i18n from "utils/i18n/i18nManager";

interface IHeaderProps {
  open: boolean;
  drawerwidth: number;
  handleDrawerOpen: () => void;
}
const includedUrls = ["/", "/categories"];

const Header: React.FunctionComponent<IHeaderProps> = ({ open, drawerwidth, handleDrawerOpen }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { isOnline } = useOnlineStatus();
  const { dispatch: updateSetting } = useSettings();
  const noSleep = React.useMemo(() => new NoSleep(), []);
  const [language, setLanguage] = React.useState<string>(i18n.language);
  const { mutateAsync: getHomePageInfoAsync } = useMutateGetHomePageInfo();
  const [isWakeLockEnabled, setIsWakeLockEnabled] = React.useState(false);
  const isSmallScreen = useMediaQuery("(max-width:370px)");
  const isWideScreen = useMediaQuery("(min-width:500px)");
  const [isOpenNoSleepModal, setOpenNoSleepModal] = React.useState<boolean | undefined>(undefined);
  const savedDcumentPdfFilesCount = useLiveQuery(() => getCountPdfDocumentsMeta(), []) ?? 0;
  const totalDocumentPdfFiles = useLiveQuery(() => getAllSynchronizationItemsMeta(), []) ?? [];
  const memoTotalDocumentPdfFiles = totalDocumentPdfFiles.reduce(
    (prev, curr) => prev + curr.offlineDocumentsVersionCount,
    0,
  );
  const numberOfDocsToSave = memoTotalDocumentPdfFiles - savedDcumentPdfFilesCount;

  const percentCalc = () => {
    let percent = (savedDcumentPdfFilesCount / memoTotalDocumentPdfFiles) * 100;
    if (percent > 100) {
      percent = 100;
    }
    return percent;
  };

  const percentage = Boolean(memoTotalDocumentPdfFiles) ? percentCalc() : 0;

  const { accounts } = useRedirectIfUnauthenticated();
  const handleRefresh = React.useCallback(() => {
    if (isPwa) {
      if (pathname === "/") {
        getHomePageInfoAsync();
      }
      updateSettings({ key: "versionsApiHasBeenCalled", value: false }).then(() =>
        getSynchronizationItemsAsync(accounts[0]?.username, {
          hasToSyncDocuments: true,
        }).then(() => window.location.reload()),
      );
    } else {
      window.location.reload();
    }
  }, [accounts, pathname, getHomePageInfoAsync]);

  const handleOnChangeWakeUp = React.useCallback(() => {
    if (!isWakeLockEnabled) {
      noSleep.enable();
      setOpenNoSleepModal(false);
      setIsWakeLockEnabled(true);
      console.info("wake up enabled");
    } else {
      noSleep.disable();
      setOpenNoSleepModal(false);
      setIsWakeLockEnabled(false);
      console.info("wake up disabled");
    }
  }, [noSleep, isWakeLockEnabled]);

  const handleLanguage = (event: React.MouseEvent<HTMLElement> | SelectChangeEvent<string>, newLanguage: string) => {
    const value =
      typeof event.target === "object" && "value" in event.target ? (event.target.value as string) : newLanguage;
    if (value) {
      setLanguage(value);
      i18n.changeLanguage(value);
      localStorage.setItem("choosedLanguage", value);
    }
  };

  React.useLayoutEffect(() => {
    if (includedUrls.includes(pathname) && isPwa && isMobileDevice && Boolean(memoTotalDocumentPdfFiles)) {
      if (
        (isOpenNoSleepModal === undefined && !isWakeLockEnabled && numberOfDocsToSave > 20) ||
        (isOpenNoSleepModal === false && isWakeLockEnabled && numberOfDocsToSave === 0)
      ) {
        setOpenNoSleepModal(true);
      }
    }
  }, [numberOfDocsToSave, memoTotalDocumentPdfFiles, pathname, isWakeLockEnabled, isOpenNoSleepModal]);

  React.useLayoutEffect(() => {
    const isPathIncluded = includedUrls.includes(pathname);
    const hasPdfFiles = Boolean(memoTotalDocumentPdfFiles);
    const noDocsToSave = numberOfDocsToSave === 0;

    const shouldDisableVersioning = isPwa && isPathIncluded && hasPdfFiles && noDocsToSave;

    if (shouldDisableVersioning) {
      updateSetting({ type: VERSIONS_API_REQUEST_ACTIONS.DISABLE });
    }
  }, [updateSetting, numberOfDocsToSave, memoTotalDocumentPdfFiles, pathname]);

  return (
    <AppBar open={open} drawerwidth={drawerwidth} color={"inherit"} position="fixed">
      <Toolbar>
        <Stack direction={"column"} alignItems={"center"} flexGrow={1} width={"100%"}>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} width={"100%"}>
            <Stack direction={"row"} alignItems={"center"}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{ mr: 1, ...(open && { display: "none" }) }}
              >
                <FontAwesomeIcon icon="bars" />
              </IconButton>
              {process.env.REACT_APP_NAME === "GEDOC" ? (
                <img alt="logo" src={LogoGEDOC} style={{ width: "50px", height: "50px", marginRight: "0.5em" }} />
              ) : (
                <img alt="logo" src={LogoGETeDOCS} style={{ width: "50px", height: "50px", marginRight: "0.5em" }} />
              )}
            </Stack>
            <Box flexGrow={1} />
            <Stack direction={"row"} alignItems={"center"}>
              {isPwa && (
                <>
                  {isWideScreen && (
                    <Box mr={1}>
                      <Notifications />
                    </Box>
                  )}
                  <Box width={"30px"} ml={1} mr={1}>
                    <CircularProgressWithLabel value={percentage} />
                  </Box>
                </>
              )}
              <Box mr={1}>
                {isOnline ? (
                  <Box component="span" sx={{ color: theme.palette.success.main, verticalAlign: "0.25em" }}>
                    <FontAwesomeIcon icon="wifi" />
                  </Box>
                ) : (
                  <SvgIcon color="error" inheritViewBox component={WifiSlash} sx={{ fontSize: "1.250rem" }} />
                )}
              </Box>
              <Button
                size="large"
                color="primary"
                variant="outlined"
                aria-label="Actualiser"
                disabled={!isOnline}
                onClick={handleRefresh}
                sx={stylesRefresh}
              >
                <FontAwesomeSvgIcon icon={svgFonts.faRotateRight} />
              </Button>
              {isSmallScreen ? (
                <FormControl variant="standard" sx={{ minWidth: 40, m: 0 }}>
                  <Select
                    value={language}
                    onChange={(e) => handleLanguage(e as SelectChangeEvent<string>, "")}
                    disableUnderline
                    size="small"
                    sx={{
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        py: 0.5,
                        px: 1,
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: "4px",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.2rem",
                      },
                    }}
                    renderValue={(value) => (
                      <SvgIcon
                        component={value === LANGUAGES.fr_FR ? FlagFR : FlagUK}
                        inheritViewBox
                        fontSize="small"
                      />
                    )}
                  >
                    <MenuItem value={LANGUAGES.fr_FR} sx={{ justifyContent: "center" }}>
                      <SvgIcon component={FlagFR} inheritViewBox fontSize="small" />
                    </MenuItem>
                    <MenuItem value={LANGUAGES.en_US} sx={{ justifyContent: "center" }}>
                      <SvgIcon component={FlagUK} inheritViewBox fontSize="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={language}
                  onChange={handleLanguage}
                  sx={{ maxHeight: "50px" }}
                  aria-label="language"
                >
                  <ToggleButton value={LANGUAGES.fr_FR} aria-label="FR">
                    <SvgIcon component={FlagFR} inheritViewBox fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value={LANGUAGES.en_US} aria-label="EN">
                    <SvgIcon component={FlagUK} inheritViewBox fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
          </Stack>
          {isPwa && !isWideScreen && (
            <Box flexGrow={1} width={"100%"} display="flex" justifyContent="end">
              <Notifications />
            </Box>
          )}
        </Stack>
      </Toolbar>
      {isOpenNoSleepModal && (
        <NoSleeepDialog
          isDialogOpen={isOpenNoSleepModal}
          onOpenDialog={setOpenNoSleepModal}
          isWakeLockEnabled={isWakeLockEnabled}
          onChangeWakeUp={handleOnChangeWakeUp}
        />
      )}
    </AppBar>
  );
};
export default Header;
